import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import disbursementsAPI from '../../api/disbursementsAPI';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';
import accountingClientsAPI from '../../api/accountingClientsAPI';
import ChartOfAccountsSearch from '../../components/ChartOfAccountsSearch';
import MoneyInput from '../../components/MoneyInput';
import VendorSearch from '../../components/VendorSearch';
import CustomerSearch from '../../components/CustomerSearch';
import PredictiveMemoInput from '../../components/PredictiveMemoInput';
import { useAuth } from '../../context/AuthContext';
import { useVendorTerms } from '../../utils/terminology';
import useCompanyContext from '../../hooks/useCompanyContext';

const NewDisbursementPage = () => {
  const term = useVendorTerms();
  const navigate = useNavigate();
  const { selectedClient } = useAuth();
  const { accountingClientId: contextAccountingClientId } = useCompanyContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountingClients, setAccountingClients] = useState([]);
  const [selectedAccountingClient, setSelectedAccountingClient] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    memo: '',
    checkNo: '',
    orNumber: '',
    payeeType: 'vendor',
    vendorId: null,
    customerId: null,
    payeeSnapshot: { name: '', tin: '', address: {} },
    lines: [{ accountId: '', accountDisplay: '', debit: 0, credit: 0, description: '' }]
  });

  const [referenceNo, setReferenceNo] = useState('');

  useEffect(() => {
    loadAccountingClients();
  }, []);

  useEffect(() => {
    if (contextAccountingClientId && contextAccountingClientId !== selectedAccountingClient) {
      setSelectedAccountingClient(contextAccountingClientId);
    }
  }, [contextAccountingClientId, selectedAccountingClient]);

  useEffect(() => {
    if (selectedAccountingClient) {
      generateReferenceNo();
    }
  }, [selectedAccountingClient]);

  const loadAccountingClients = async () => {
    try {
      setContextLoading(true);
      const response = await accountingClientsAPI.getActive();
      const clients = response.data || response || [];
      setAccountingClients(clients);
      
      // Get selected client from context → localStorage → auto-select first
      const storedClientId = contextAccountingClientId || selectedClient?._id || localStorage.getItem('accountingClientId');
      if (storedClientId && clients.some(c => c._id === storedClientId)) {
        setSelectedAccountingClient(storedClientId);
      } else if (clients.length > 0) {
        setSelectedAccountingClient(clients[0]._id);
        localStorage.setItem('accountingClientId', clients[0]._id);
      }
      
      fetchInitialData();
    } catch (err) {
      console.error('Failed to load accounting clients:', err);
      setError('Failed to load accounting clients');
      fetchInitialData();
    } finally {
      setContextLoading(false);
    }
  };

  const handleAccountingClientSelect = (clientId) => {
    setSelectedAccountingClient(clientId);
    localStorage.setItem('accountingClientId', clientId);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const accountsRes = await chartOfAccountsAPI.getAll();
      setAccounts(accountsRes.data || []);
    } catch (err) {
      setError('Failed to load initial data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateReferenceNo = async () => {
    if (!selectedAccountingClient) return;

    try {
      const res = await disbursementsAPI.generateReferenceNo(selectedAccountingClient);
      setReferenceNo(res.data?.referenceNo || '');
    } catch (err) {
      console.error('Failed to generate reference number:', err);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePayeeSnapshotChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payeeSnapshot: {
        ...prev.payeeSnapshot,
        [field]: value
      }
    }));
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payeeSnapshot: {
        ...prev.payeeSnapshot,
        address: {
          ...prev.payeeSnapshot.address,
          [field]: value
        }
      }
    }));
  };

  const handleLineChange = (index, field, value) => {
    setFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = {
        ...newLines[index],
        [field]: value
      };
      return { ...prev, lines: newLines };
    });
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { accountId: '', accountDisplay: '', debit: 0, credit: 0, description: '' }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const validatePayee = () => {
    const { payeeType, vendorId, customerId, payeeSnapshot } = formData;

    if (payeeType === 'vendor') {
      const id = vendorId?._id || vendorId;
      return id ? '' : `${term.one} is required — pick one from the search results`;
    }

    if (payeeType === 'customer') {
      const id = customerId?._id || customerId;
      if (id) return '';
      return payeeSnapshot?.name?.trim()
        ? ''
        : 'Customer name is required for customer payee type';
    }

    return payeeSnapshot?.name?.trim()
      ? ''
      : `Payee name is required for ${payeeType} payee type`;
  };

  const handleCreate = async () => {
    if (!selectedAccountingClient) {
      setError('Accounting client is required');
      return;
    }

    if (formData.lines.length === 0) {
      setError('At least one line is required');
      return;
    }

    // Mirror the server-side create rules so the user sees the real reason
    // immediately instead of a generic 400 after the round trip.
    const payeeError = validatePayee();
    if (payeeError) {
      setError(payeeError);
      return;
    }

    if (formData.lines.some((line) => !line.accountId)) {
      setError('Every line must have an account selected from the search results');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        accountingClient: selectedAccountingClient,
        date: formData.date,
        memo: formData.memo,
        checkNo: formData.checkNo,
        orNumber: formData.orNumber,
        payeeType: formData.payeeType,
        vendorId: formData.payeeType === 'vendor' ? (formData.vendorId?._id || formData.vendorId) : null,
        customerId: formData.payeeType === 'customer' ? (formData.customerId?._id || formData.customerId) : null,
        payeeSnapshot: ['vendor', 'customer'].includes(formData.payeeType) ? undefined : formData.payeeSnapshot,
        lines: formData.lines.map((line) => ({
          accountId: line.accountId,
          debit: line.debit,
          credit: line.credit,
          description: line.description
        }))
      };

      const res = await disbursementsAPI.create(payload);
      navigate(`/disbursements/${res.data._id}`);
    } catch (err) {
      setError(err.message || 'Failed to create disbursement');
    } finally {
      setLoading(false);
    }
  };

  const getTotalDebits = () => {
    return formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  };

  const getTotalCredits = () => {
    return formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  };

  const isBalanced = () => Math.abs(getTotalDebits() - getTotalCredits()) < 0.01;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const disabledUI = loading;

  // Compute memo context for predictive input
  const memoContext = useMemo(() => {
    const accountIds = formData.lines
      .map(l => l.accountId)
      .filter(Boolean);
    const uniqueAccountIds = [...new Set(accountIds)];
    
    const accountNames = formData.lines
      .map(l => l.accountDisplay)
      .filter(Boolean)
      .slice(0, 2);
    
    // Determine vendor/customer info
    let vendorId = null;
    let vendorName = null;
    
    if (formData.payeeType === 'vendor' && formData.vendorId) {
      vendorId = formData.vendorId;
      vendorName = formData.payeeSnapshot?.name || null;
    } else if (formData.payeeType === 'customer' && formData.customerId) {
      vendorName = formData.payeeSnapshot?.name || null;
    }
    
    return {
      entryDate: formData.date,
      accounts: uniqueAccountIds,
      accountNames,
      vendorId,
      vendorName,
      entityType: 'disbursement'
    };
  }, [formData.lines, formData.date, formData.payeeType, formData.vendorId, formData.customerId, formData.payeeSnapshot]);

  const handleChartOfAccountSelect = (account, index) => {
    if (!account) {
      setFormData(prev => {
        const newLines = [...prev.lines];
        newLines[index] = {
          ...newLines[index],
          accountId: '',
          accountDisplay: ''
        };
        return { ...prev, lines: newLines };
      });
      return;
    }
    setFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = {
        ...newLines[index],
        accountId: account._id,
        accountDisplay: `${account.accountNumber} - ${account.accountName}`
      };
      return { ...prev, lines: newLines };
    });
  };

  return (
    <Layout
      contextKind="accounting_client"
      contextOptions={accountingClients}
      activeContextId={selectedAccountingClient}
      onContextSelect={handleAccountingClientSelect}
    >
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        {/* Page Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">
            <a href="/disbursements" className="text-teal-600 hover:underline">Disbursements</a> / New
          </h1>
        </div>

        {/* Error Alert */}
        {!!error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Balance Warning */}
        {!isBalanced() && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            Disbursement is out of balance by ₱{Math.abs(getTotalDebits() - getTotalCredits()).toFixed(2)}
          </div>
        )}

        {/* DV HEADER SECTION */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Voucher Header</h2>

          {/* Reference No & Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Reference No</label>
              <input
                type="text"
                value={referenceNo}
                className="w-full border rounded px-3 py-2 text-gray-600 bg-gray-100"
                disabled
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleFieldChange}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                required
                disabled={disabledUI}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Particulars *</label>
              <PredictiveMemoInput
                value={formData.memo}
                onChange={(memo) => setFormData(prev => ({ ...prev, memo }))}
                disabled={disabledUI}
                required
                placeholder="Required"
                accountingClientId={selectedAccountingClient}
                context={memoContext}
              />
            </div>
          </div>

          {/* Check No & OR Number Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">Check No</label>
              <input
                type="text"
                name="checkNo"
                value={formData.checkNo}
                onChange={handleFieldChange}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={disabledUI}
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">OR Number</label>
              <input
                type="text"
                name="orNumber"
                value={formData.orNumber}
                onChange={handleFieldChange}
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={disabledUI}
              />
            </div>
          </div>

          {/* PAYEE INFORMATION - NESTED UNDER HEADER */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Payee Information</h3>

            <div className="mb-3">
              <label className="block text-gray-600 text-xs font-medium mb-1">Payee Type *</label>
              <select
                name="payeeType"
                value={formData.payeeType}
                onChange={handleFieldChange}
                className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={disabledUI}
              >
                <option value="vendor">{term.one}</option>
                <option value="customer">Customer</option>
                <option value="employee">Employee</option>
                <option value="government">Government</option>
                <option value="other">Other</option>
              </select>
            </div>

            {formData.payeeType === 'vendor' && (
              <div className="mb-3">
                <label className="block text-gray-600 text-xs font-medium mb-1">{term.one}</label>
                <VendorSearch
                  value={formData.vendorId}
                  onSelect={(vendor) => setFormData(prev => ({ ...prev, vendorId: vendor }))}
                  disabled={disabledUI}
                />
              </div>
            )}

            {formData.payeeType === 'customer' && (
              <div className="mb-3">
                <label className="block text-gray-600 text-xs font-medium mb-1">Customer</label>
                <CustomerSearch
                  value={formData.customerId}
                  onSelect={(customer) => {
                    if (customer) {
                      setFormData(prev => ({
                        ...prev,
                        customerId: customer,
                        payeeSnapshot: {
                          name: customer.name || '',
                          tin: customer.email || '',
                          address: {
                            street: customer.address || '',
                            city: customer.city || '',
                            province: ''
                          }
                        }
                      }));
                    } else {
                      setFormData(prev => ({ ...prev, customerId: null }));
                    }
                  }}
                  disabled={disabledUI}
                />
              </div>
            )}

            {!['vendor', 'customer'].includes(formData.payeeType) && (
              <div className="space-y-2">
                <div>
                  <label className="block text-gray-600 text-xs font-medium mb-1">Payee Name *</label>
                  <input
                    type="text"
                    value={formData.payeeSnapshot.name}
                    onChange={(e) => handlePayeeSnapshotChange('name', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                    disabled={disabledUI}
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-xs font-medium mb-1">TIN (optional)</label>
                  <input
                    type="text"
                    value={formData.payeeSnapshot.tin || ''}
                    onChange={(e) => handlePayeeSnapshotChange('tin', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={disabledUI}
                  />
                </div>

                <div>
                  <label className="block text-gray-600 text-xs font-medium mb-1">Street Address</label>
                  <input
                    type="text"
                    placeholder="Street"
                    value={formData.payeeSnapshot.address?.street || ''}
                    onChange={(e) => handleAddressChange('street', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={disabledUI}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-gray-600 text-xs font-medium mb-1">City</label>
                    <input
                      type="text"
                      placeholder="City"
                      value={formData.payeeSnapshot.address?.city || ''}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      disabled={disabledUI}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 text-xs font-medium mb-1">Province</label>
                    <input
                      type="text"
                      placeholder="Province"
                      value={formData.payeeSnapshot.address?.province || ''}
                      onChange={(e) => handleAddressChange('province', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      disabled={disabledUI}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* DISBURSEMENT LINES SECTION */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Disbursement Lines</h2>
            <button
              type="button"
              onClick={addLine}
              className="px-3 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center text-sm"
              disabled={disabledUI}
            >
              <span className="mr-1">+</span> Add Line
            </button>
          </div>

          <div className="overflow-x-auto min-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account *
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Debit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credit
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {formData.lines.map((line, index) => (
                  <tr key={index} className="hover:bg-gray-50 align-top">
                    <td className="px-4 py-3">
                      <ChartOfAccountsSearch
                        value={line.accountDisplay || ''}
                        onChange={(value) => handleLineChange(index, 'accountDisplay', value)}
                        onSelect={(account) => handleChartOfAccountSelect(account, index)}
                        placeholder="Search chart of accounts..."
                        showLinkedBadge={false}
                        disabled={disabledUI}
                      />
                    </td>

                    <td className="px-4 py-3 text-right">
                      <MoneyInput
                        value={line.debit}
                        onValueChange={(value) => handleLineChange(index, 'debit', value)}
                        disabled={disabledUI}
                      />
                    </td>

                    <td className="px-4 py-3 text-right">
                      <MoneyInput
                        value={line.credit}
                        onValueChange={(value) => handleLineChange(index, 'credit', value)}
                        disabled={disabledUI}
                      />
                    </td>

                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Optional description"
                        disabled={disabledUI}
                      />
                    </td>

                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                        disabled={formData.lines.length === 1 || disabledUI}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="border-t mt-4 pt-4">
            <div className="flex justify-end gap-8">
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Debits:</p>
                <p className="text-lg font-semibold">{formatCurrency(getTotalDebits())}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Total Credits:</p>
                <p className="text-lg font-semibold">{formatCurrency(getTotalCredits())}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Difference:</p>
                <p className={`text-lg font-semibold ${isBalanced() ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(Math.abs(getTotalDebits() - getTotalCredits()))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate('/disbursements')}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
              disabled={disabledUI}
            >
              Cancel
            </button>

            <button
              onClick={handleCreate}
              disabled={loading || !selectedAccountingClient}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Disbursement'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewDisbursementPage;
